import {
    Competitor,
    CompetitorScope,
    StoredCompetitor,
} from '../types/competitors';

const databaseName = 'zitoneo-competitors';
const storeName = 'hotelCompetitors';

const requireId = (value: string, name: string) => {
    if (typeof value !== 'string' || !value.trim()) {
        throw new Error(`Missing ${name} for competitor storage.`);
    }
};

const validateScope = ({ ownerId, hotelId }: CompetitorScope) => {
    requireId(ownerId, 'owner ID');
    requireId(hotelId, 'hotel ID');
};

const validateCompetitor = (competitor: Competitor) => {
    requireId(competitor.id, 'competitor ID');
    requireId(competitor.name, 'competitor name');
    requireId(competitor.query, 'competitor query');
    if (!['manual', 'suggested'].includes(competitor.source)) {
        throw new Error('Invalid competitor source.');
    }
    if (
        [competitor.city, competitor.country, competitor.starRating].some(
            value => value !== undefined && typeof value !== 'string'
        )
    ) {
        throw new Error('Invalid competitor details.');
    }
};

const openDatabase = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const request = indexedDB.open(databaseName, 1);
        let rejected = false;
        request.onupgradeneeded = () => {
            const store = request.result.createObjectStore(storeName, {
                keyPath: ['ownerId', 'hotelId', 'id'],
            });
            store.createIndex('owner', 'ownerId');
            store.createIndex('ownerHotel', ['ownerId', 'hotelId']);
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => {
            rejected = true;
            reject(
                new Error(
                    'Competitor database upgrade is blocked by another tab.'
                )
            );
        };
        request.onsuccess = () => {
            const database = request.result;
            if (rejected) {
                database.close();
            } else {
                database.onversionchange = () => database.close();
                resolve(database);
            }
        };
    });

const runTransaction = async <T>(
    mode: IDBTransactionMode,
    execute: (
        store: IDBObjectStore,
        setResult: (value: T) => void,
        fail: (error: unknown) => void
    ) => void
): Promise<T> => {
    const database = await openDatabase();
    try {
        return await new Promise<T>((resolve, reject) => {
            const transaction = database.transaction(storeName, mode);
            let result: T;
            transaction.oncomplete = () => resolve(result);
            transaction.onabort = () =>
                reject(
                    transaction.error ||
                        new Error('Competitor storage transaction aborted.')
                );
            const fail = (error: unknown) => {
                transaction.abort();
                reject(error);
            };
            try {
                execute(
                    transaction.objectStore(storeName),
                    value => {
                        result = value;
                    },
                    fail
                );
            } catch (error) {
                fail(error);
            }
        });
    } finally {
        database.close();
    }
};

const validateStoredCompetitor = (value: StoredCompetitor) => {
    validateScope(value);
    validateCompetitor(value);
    if (typeof value.updatedAt !== 'string') {
        throw new Error('Invalid saved competitor data.');
    }
};

/**
 * Loads the previously searched competitors for one owner and hotel.
 * @param scope Owner and own hotel identifiers.
 * @returns Persisted competitors belonging only to the requested scope.
 */
export const getHotelCompetitors = async (
    scope: CompetitorScope
): Promise<StoredCompetitor[]> => {
    validateScope(scope);
    const entries = await runTransaction<StoredCompetitor[]>(
        'readonly',
        (store, setResult) => {
            const request = store
                .index('ownerHotel')
                .getAll([scope.ownerId, scope.hotelId]);
            request.onsuccess = () => setResult(request.result);
        }
    );
    entries.forEach(validateStoredCompetitor);
    return entries;
};

/**
 * Loads all competitors and hotel comparisons belonging to an owner.
 * @param ownerId Owner identifier shared by the owner's users.
 * @returns Records grouped by their hotelId and competitor id.
 */
export const getOwnerCompetitors = async (
    ownerId: string
): Promise<StoredCompetitor[]> => {
    requireId(ownerId, 'owner ID');
    const entries = await runTransaction<StoredCompetitor[]>(
        'readonly',
        (store, setResult) => {
            const request = store.index('owner').getAll(ownerId);
            request.onsuccess = () => setResult(request.result);
        }
    );
    entries.forEach(validateStoredCompetitor);
    return entries;
};

const saveEntries = async (
    scope: CompetitorScope,
    competitors: Competitor[]
) => {
    validateScope(scope);
    competitors.forEach(validateCompetitor);
    return runTransaction<StoredCompetitor[]>(
        'readwrite',
        (store, setResult) => {
            const now = new Date().toISOString();
            const records = competitors.map(competitor => {
                const record: StoredCompetitor = {
                    ...competitor,
                    ...scope,
                    updatedAt: now,
                };
                store.put(record);
                return record;
            });
            setResult(records);
        }
    );
};

/**
 * Adds or updates suggestions without overwriting existing analyses.
 * @param scope Owner and own hotel identifiers.
 * @param competitors Candidates with stable identifiers supplied by the caller.
 * @returns Resolves once all entries are committed atomically.
 */
export const saveHotelCompetitors = async (
    scope: CompetitorScope,
    competitors: Competitor[]
) => {
    await saveEntries(scope, competitors);
};

/**
 * Removes a competitor and analysis from one hotel, preserving other scopes.
 * @param scope Owner and own hotel identifiers.
 * @param competitorId Competitor identifier.
 * @returns Resolves after the deletion commits.
 */
export const removeHotelCompetitor = async (
    scope: CompetitorScope,
    competitorId: string
) => {
    validateScope(scope);
    requireId(competitorId, 'competitor ID');
    await runTransaction<void>('readwrite', (store, setResult) => {
        store.delete([scope.ownerId, scope.hotelId, competitorId]);
        setResult(undefined);
    });
};
